import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, Search, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PRIMARY_NAV, SEARCH_INDEX } from "./content/nav";
import { track, EVENTS } from "./lib/analytics";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function Dropdown({ item, open, setOpen }) {
  const ref = useRef(null);
  const id = `nav-${slug(item.label)}`;
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(null); };
    document.addEventListener("pointerdown", onDoc);
    return () => document.removeEventListener("pointerdown", onDoc);
  }, [setOpen]);
  const isOpen = open === item.label;
  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(item.label)} onMouseLeave={() => setOpen(null)}>
      <button
        data-testid={`${id}-trigger`} aria-haspopup="true" aria-expanded={isOpen} aria-controls={`${id}-menu`}
        onClick={() => setOpen(isOpen ? null : item.label)}
        onKeyDown={(e) => { if (e.key === "Escape") setOpen(null); }}
        className={`nav-label whitespace-nowrap inline-flex items-center gap-1 px-3 py-2 rounded-md transition-colors hover:text-[#1A6EFF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A6EFF] ${isOpen ? "text-[#1A6EFF]" : "text-[#0A1628]"}`}
      >
        {item.label} <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <div id={`${id}-menu`} role="menu" hidden={!isOpen}
        className="absolute left-0 top-full pt-3 w-[380px] fade-up">
        <div className="rounded-2xl border border-border bg-white depth-shadow-lg p-2 grid">
          <Link to={item.to} role="menuitem" onClick={() => setOpen(null)} data-testid={`${id}-overview`}
            className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-semibold text-[#0A1628] hover:bg-[#F0F4FF] focus-visible:bg-[#F0F4FF] outline-none">
            All {item.label} <ArrowRight className="h-4 w-4 text-[#1A6EFF]" />
          </Link>
          <div className="h-px bg-border my-1" />
          {item.children.map((c) => (
            <Link key={c.label} to={c.to} role="menuitem" onClick={() => setOpen(null)} data-testid={`${id}-${slug(c.label)}`}
              className="px-3 py-2 rounded-lg hover:bg-[#F0F4FF] focus-visible:bg-[#F0F4FF] outline-none transition-colors">
              <div className="text-sm font-medium text-[#0A1628]">{c.label}</div>
              {c.desc && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{c.desc}</div>}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function SiteSearch() {
  const [q, setQ] = useState(""); const [focus, setFocus] = useState(false);
  const nav = useNavigate();
  const results = q.trim().length > 1 ? SEARCH_INDEX.filter((r) => r.label.toLowerCase().includes(q.toLowerCase())).slice(0, 7) : [];
  const go = (to) => { track(EVENTS.SEARCH, { query: q }); setQ(""); setFocus(false); nav(to); };
  return (
    <div className="relative hidden 2xl:block">
      <label htmlFor="site-search" className="sr-only">Search the site</label>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      <input id="site-search" data-testid="site-search-input" value={q} onChange={(e) => setQ(e.target.value)}
        onFocus={() => setFocus(true)} onBlur={() => setTimeout(() => setFocus(false), 150)}
        onKeyDown={(e) => { if (e.key === "Enter" && results[0]) go(results[0].to); if (e.key === "Escape") setFocus(false); }}
        placeholder="Explore Intelligence..." autoComplete="off"
        className={`h-10 rounded-full border border-border bg-[#F8FAFF] pl-9 pr-4 text-sm outline-none transition-[width,box-shadow] duration-300 focus:ring-2 focus:ring-[#1A6EFF]/40 ${focus ? "w-[320px]" : "w-[240px]"}`} />
      {focus && results.length > 0 && (
        <div role="listbox" data-testid="site-search-results" className="absolute right-0 top-full mt-2 w-[320px] rounded-xl border border-border bg-white depth-shadow-lg p-1.5 fade-up">
          {results.map((r) => (
            <button key={r.to + r.label} role="option" aria-selected="false" onMouseDown={() => go(r.to)}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#F0F4FF] flex items-center justify-between">
              <span className="text-sm text-[#0A1628]">{r.label}</span><span className="eyebrow text-muted-foreground">{r.group}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileNav() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="mobile-menu-trigger" aria-label="Open navigation menu" className="lg:hidden h-11 w-11 grid place-items-center rounded-md hover:bg-[#F0F4FF] focus-visible:ring-2 focus-visible:ring-[#1A6EFF] outline-none">
          <Menu className="h-6 w-6" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[88vw] sm:w-[400px] p-0 overflow-y-auto" data-testid="mobile-menu-drawer">
        <SheetTitle className="sr-only">Navigation</SheetTitle>
        <div className="px-6 pt-6 pb-4 border-b border-border"><img src="/logo.png" alt="TechAI Fortune" className="h-10 w-auto" /></div>
        <nav className="px-3 py-3">
          <Accordion type="multiple" className="w-full">
            {PRIMARY_NAV.map((item) => item.children ? (
              <AccordionItem key={item.label} value={item.label} className="border-b border-border/70">
                <AccordionTrigger data-testid={`mobile-nav-${slug(item.label)}`} className="nav-label px-3 py-4 hover:no-underline text-[#0A1628]">{item.label}</AccordionTrigger>
                <AccordionContent className="pb-2">
                  <Link to={item.to} className="block px-3 py-3 text-sm font-semibold text-[#1A6EFF] min-h-[44px]">All {item.label}</Link>
                  {item.children.map((c) => (
                    <Link key={c.label} to={c.to} className="block px-3 py-3 text-sm text-[#0A1628]/80 hover:text-[#1A6EFF] min-h-[44px]">{c.label}</Link>
                  ))}
                </AccordionContent>
              </AccordionItem>
            ) : (
              <Link key={item.label} to={item.to} data-testid={`mobile-nav-${slug(item.label)}`} className="nav-label block px-3 py-4 border-b border-border/70 text-[#0A1628] min-h-[44px]">{item.label}</Link>
            ))}
          </Accordion>
        </nav>
        <div className="px-6 py-6 flex flex-col gap-3">
          <Link to="/contact"><Button className="w-full h-12 rounded-full bg-[#1A6EFF] hover:bg-[#0F5AE0]">Book a Consultation</Button></Link>
          <Link to="/app"><Button variant="outline" className="w-full h-12 rounded-full">Client Portal</Button></Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Header() {
  const [open, setOpen] = useState(null);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(null); }, [pathname]);
  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-border" data-testid="site-header">
      <div className="container mx-auto px-6 md:px-10 lg:px-16 h-16 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-brand-link" className="flex items-center shrink-0">
          <img src="/logo.png" alt="TechAI Fortune — IT Consulting & Staffing" data-testid="nav-brand-logo" className="h-11 md:h-12 w-auto" />
        </Link>
        <nav aria-label="Primary" className="hidden lg:flex items-center gap-0.5" data-testid="primary-nav">
          {PRIMARY_NAV.map((item) => item.children ? (
            <Dropdown key={item.label} item={item} open={open} setOpen={setOpen} />
          ) : (
            <NavLink key={item.label} to={item.to} data-testid={`nav-${slug(item.label)}`}
              className={({ isActive }) => `nav-label px-3 py-2 rounded-md transition-colors hover:text-[#1A6EFF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A6EFF] ${isActive ? "text-[#1A6EFF]" : "text-[#0A1628]"}`}>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <SiteSearch />
          <Link to="/contact" className="hidden sm:block" onClick={() => track(EVENTS.CTA_CLICK, { label: "Header Contact" })}>
            <Button data-testid="header-contact-btn" className="nav-label h-10 px-5 rounded-full bg-[#1A6EFF] hover:bg-[#0F5AE0] shadow-[0_6px_20px_rgba(26,110,255,0.35)]">Contact</Button>
          </Link>
          <MobileNav />
        </div>
      </div>
    </header>
  );
}
